import { DeepMap, FieldValues } from 'react-hook-form'

import { UpdateAlarmInput } from '@/graphql/generated/schemas'

import { AlarmDataTypeI } from '../../types/types'

/**
 * Constructs the input object for the UpdateAlarm mutation
 * @param id - The device/alarm ID (they share the same UUID)
 * @param values - Form values from the alarm edit form
 * @param dirtyFields - Object containing dirty field flags from react-hook-form
 * @returns Formatted input for UpdateAlarm mutation
 */
export const getAlarmInputs = (
  id: string,
  values: AlarmDataTypeI,
  dirtyFields: DeepMap<FieldValues, true>
): UpdateAlarmInput => {
  return {
    id,
    externalId: values.externalId,
    // alarmTypeId is a NullableId tri-state: omitting the wrapper leaves the
    // stored value alone, while { value: null } clears it. Only send it when the
    // user actually changed the field — the select is feature flagged, so an
    // unconditional wrapper would clear the alarm type on unrelated edits.
    ...(dirtyFields.alarmTypeId && {
      alarmTypeId: { value: values.alarmTypeId?.value ?? null },
    }),
  }
}

/**
 * Checks if any device-level fields (name, location) have been modified
 * @param dirtyFields - Object containing dirty field flags from react-hook-form
 * @returns true if device fields are dirty, false otherwise
 */
export const isDeviceFieldsDirty = (
  dirtyFields: DeepMap<FieldValues, true>
): boolean => {
  return Object.keys(dirtyFields).some(
    (key) => key === 'name' || key === 'location'
  )
}

/**
 * Checks if any alarm-specific fields (externalId, alarmTypeId) have been modified
 * @param dirtyFields - Object containing dirty field flags from react-hook-form
 * @returns true if alarm fields are dirty, false otherwise
 */
export const isAlarmFieldsDirty = (
  dirtyFields: DeepMap<FieldValues, true>
): boolean => {
  return Object.keys(dirtyFields).some(
    (key) => key === 'externalId' || key === 'alarmTypeId'
  )
}
